(function () {
  const modal = document.querySelector('[data-quote-request-modal]');
  if (!modal) return;

  const openButtons = document.querySelectorAll('[data-quote-request-open]');
  const overlay = modal.querySelector('[data-quote-request-overlay]');
  const closeButton = modal.querySelector('[data-quote-request-close]');
  const form = modal.querySelector('.quote-request-form');
  const status = modal.querySelector('[data-quote-request-status]');
  const pageStatus = document.querySelector('[data-quote-request-page-status]');
  const submitButton = modal.querySelector('[data-quote-request-submit]');
  let lastFocusedElement = null;

  const messages = {
    name: modal.dataset.errorName || 'Please enter your name.',
    email: modal.dataset.errorEmail || 'Please enter a valid email address.',
    phone: modal.dataset.errorPhone || 'Please enter your phone number.',
    comment: modal.dataset.errorComment || 'Please enter a message.',
    generic: modal.dataset.errorGeneric || 'Please complete all required fields.',
    success: modal.dataset.successMessage || 'Thank you! Your quote request has been sent.'
  };

  const focusableSelectors =
    'a[href], button:not([disabled]):not([aria-hidden="true"]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusableElements() {
    return Array.from(modal.querySelectorAll(focusableSelectors)).filter((element) => element.offsetParent !== null);
  }

  function updateModalState(isOpen) {
    if (isOpen) {
      modal.removeAttribute('hidden');
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('quote-request-modal--open');
      document.documentElement.classList.add('quote-modal-open');
      lastFocusedElement = document.activeElement;
      const focusables = getFocusableElements();
      if (focusables.length) {
        window.setTimeout(() => focusables[0].focus(), 20);
      }
    } else {
      modal.setAttribute('hidden', '');
      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('quote-request-modal--open');
      document.documentElement.classList.remove('quote-modal-open');
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    }

    openButtons.forEach((button) => {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function closeModal() {
    updateModalState(false);
    clearFieldErrors();
    clearModalClientSummary();
  }

  function trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusables = getFocusableElements();
    if (!focusables.length) return;

    const firstFocusable = focusables[0];
    const lastFocusable = focusables[focusables.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else if (document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }

  function quoteAttr(name) {
    return name.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function fieldByName(name) {
    return form.querySelector(`[name="${quoteAttr(name)}"]`);
  }

  function setFormError(fieldName, message) {
    const field = fieldByName(fieldName);
    const errorElement = form.querySelector(`[data-quote-request-error-for="${quoteAttr(fieldName)}"]`);
    if (field) field.setAttribute('aria-invalid', 'true');
    if (errorElement) errorElement.textContent = message;
  }

  function showModalClientSummary(message) {
    if (!status || !message) return;
    status.textContent = message;
    status.classList.add('quote-request-form__status--visible', 'quote-request-form__status--error');
    status.classList.remove('quote-request-form__status--success');
  }

  function clearModalClientSummary() {
    if (!status) return;
    if (status.childElementCount > 0) return;
    status.textContent = '';
    status.classList.remove(
      'quote-request-form__status--visible',
      'quote-request-form__status--error',
      'quote-request-form__status--success'
    );
  }

  function clearFieldErrors() {
    form.querySelectorAll('[data-quote-request-error-for]').forEach((element) => {
      element.textContent = '';
    });
    form.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
  }

  function buildContactBody() {
    const message = (fieldByName('quote_message')?.value ?? '').trim();
    const productName = modal.dataset.productName || '';
    const productUrl = modal.dataset.productUrl || '';
    const marketCountry = modal.dataset.marketCountry || '';

    return [
      `Market/Country: ${marketCountry}`,
      `Product: ${productName}`,
      `Product URL: ${productUrl}`,
      '',
      'Message:',
      message
    ].join('\n');
  }

  const SUCCESS_HIDE_MS = 40000;
  let successHideTimer = null;

  function showPageStatus(message, isError) {
    if (!pageStatus || !message) return;
    pageStatus.textContent = message;
    pageStatus.classList.toggle('quote-request-form__status--error', isError);
    pageStatus.classList.toggle('quote-request-form__status--success', !isError);
    pageStatus.classList.toggle('quote-request-form__status--visible', message.length > 0);
  }

  function hidePageStatus() {
    if (!pageStatus) return;
    pageStatus.textContent = '';
    pageStatus.classList.remove(
      'quote-request-form__status--visible',
      'quote-request-form__status--success',
      'quote-request-form__status--error'
    );
  }

  function clearQuoteUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('quote_posted');
    url.hash = '';
    const search = url.searchParams.toString();
    const cleanUrl = url.pathname + (search ? `?${search}` : '');
    window.history.replaceState(window.history.state, '', cleanUrl);
  }

  function validateForm() {
    clearFieldErrors();
    clearModalClientSummary();
    let valid = true;

    const name = (fieldByName('contact[name]')?.value ?? '').trim();
    const email = (fieldByName('contact[email]')?.value ?? '').trim();
    const phone = (fieldByName('contact[phone]')?.value ?? '').trim();
    const message = (fieldByName('quote_message')?.value ?? '').trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^\+?[0-9()\-\s]{7,25}$/;

    if (!name) {
      setFormError('contact[name]', messages.name);
      valid = false;
    }

    if (!email || !emailPattern.test(email)) {
      setFormError('contact[email]', messages.email);
      valid = false;
    }

    if (!phone || !phonePattern.test(phone)) {
      setFormError('contact[phone]', messages.phone);
      valid = false;
    }

    if (!message) {
      setFormError('quote_message', messages.comment);
      valid = false;
    }

    return valid;
  }

  function setSubmitLoading(isLoading) {
    if (!submitButton) return;
    submitButton.disabled = isLoading;
    submitButton.classList.toggle('quote-request-form__submit--loading', isLoading);
  }

  function handleSubmit(event) {
    if (!validateForm()) {
      event.preventDefault();
      showModalClientSummary(messages.generic);
      const firstInvalid = form.querySelector('[aria-invalid="true"]');
      if (firstInvalid && typeof firstInvalid.focus === 'function') {
        firstInvalid.focus();
      }
      return;
    }

    clearModalClientSummary();

    const bodyInput = form.querySelector('[data-quote-request-body]');
    if (bodyInput) {
      bodyInput.value = buildContactBody();
    }

    setSubmitLoading(true);
  }

  function hasModalErrors() {
    return Boolean(status && status.classList.contains('quote-request-form__status--error'));
  }

  function handlePostRedirect() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('quote_posted') !== 'true') return;

    clearQuoteUrl();

    if (hasModalErrors()) {
      updateModalState(true);
      return;
    }

    showPageStatus(messages.success, false);
    if (pageStatus) {
      pageStatus.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    if (successHideTimer) window.clearTimeout(successHideTimer);
    successHideTimer = window.setTimeout(hidePageStatus, SUCCESS_HIDE_MS);
  }

  openButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      updateModalState(true);
    });
  });

  overlay.addEventListener('click', closeModal);
  closeButton.addEventListener('click', closeModal);

  document.addEventListener('keydown', (event) => {
    if (modal.getAttribute('aria-hidden') === 'true') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
    }
    trapFocus(event);
  });

  if (form) {
    form.setAttribute('novalidate', '');
    form.addEventListener('submit', handleSubmit);
  }

  handlePostRedirect();
})();
